import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { ButtonBase, InputBase, InputSelect } from "../../../common/components/form";
import i18n from "../../../common/lib/i18n/i18n.js";
import { useGetLocales, useReduxAccount } from "../../hooks";
import * as API from "../../lib/api.js";
import * as Links from "../../lib/links.js";
import { setAccount } from "../../store/panel.js";
import Confirm from "../Confirm";
import Wait from "../Wait";

export default function Account() {
    const { t } = useTranslation();

    const history = useHistory();
    const dispatch = useDispatch();
    const reduxAccount = useReduxAccount();

    const [wait, setWait] = React.useState(false);
    const [accountData, setAccountData] = React.useState({});
    const [password, setPassword] = React.useState({});
    const [prevAccountData, setPrevAccountData] = React.useState("");
    const [errors, setErrors] = React.useState({});

    const [ConfirmDelete, setConfirmDelete] = React.useState({ show: false });

    const locales = useGetLocales();

    React.useEffect(() => {
        const data = reduxAccount || {};

        setAccountData(data);
        setPassword({});
        setPrevAccountData(JSON.stringify(data));
    }, [reduxAccount]);

    const selectedLang = i18n.language;
    const handleLanguageChange = lang => {
        API.Send("/lang/set", { lang }).then(res => {
            const { lang } = res;

            i18n.changeLanguage(lang);
        });
    };

    function onChange(event) {
        let { name, value } = event.target;

        const _errors = { ...errors };

        switch (name) {
            case "firstname":
                if (value) {
                    value = value
                        .split(" ")
                        .map(v => v[0].toUpperCase() + v.slice(1).toLowerCase())
                        .join(" ");
                }

                if (!value || !value.trim()) {
                    _errors[name] = "ErrorFieldFirstnameEnter";
                } else {
                    _errors[name] = null;
                }

                setAccountData({ ...accountData, [name]: value });

                break;

            case "lastname":
                if (value) {
                    value = value
                        .split(" ")
                        .map(v => v[0].toUpperCase() + v.slice(1).toLowerCase())
                        .join(" ");
                }

                _errors[name] = null;

                if (!value || !value.trim()) {
                    _errors[name] = "ErrorFieldLastnameEnter";
                }

                setAccountData({ ...accountData, [name]: value });

                break;

            case "email":
                _errors[name] = null;
                value = value.toLowerCase();

                if (!value || !value.trim()) {
                    _errors[name] = "ErrorFieldEMailEnter";
                } else if (!/^[^@]+\@[^@\.]+(\.[^\@\.]+)+$/.test(value.trim())) {
                    _errors[name] = "ErrorFieldEMailInvalid";
                }

                setAccountData({ ...accountData, [name]: value });

                break;

            case "password":
                _errors[name] = null;

                if (!value) {
                    _errors[name] = "ErrorFieldPasswordEnter";
                    _errors.password2 = null;
                } else if (value.length < 6) {
                    _errors[name] = "ErrorFieldPasswordTooShort";
                    _errors.password2 = null;
                } else if (!password.password2 || !password.password2) {
                    _errors.password2 = "ErrorFieldPasswordConfirm";
                    _errors[name] = null;
                } else if (value !== password.password2) {
                    _errors[name] = "ErrorFieldPasswordsDoNotMatch";
                    _errors.password2 = "ErrorFieldPasswordsDoNotMatch";
                }

                setPassword({ ...password, password: value });

                break;

            case "password2":
                _errors[name] = null;
                _errors.password = null;

                if (!password.password || _errors.password) {
                    _errors[name] = null;
                } else if (value.length < 6) {
                    _errors.password = "ErrorFieldPasswordTooShort";
                } else if (!value || !value) {
                    _errors[name] = "ErrorFieldPasswordConfirm";
                } else if (value !== password.password) {
                    _errors[name] = "ErrorFieldPasswordsDoNotMatch";
                    _errors.password = "ErrorFieldPasswordsDoNotMatch";
                }

                setPassword({ ...password, password2: value });

                break;
        }

        setErrors(_errors);
    }

    /* function onCancelClick() {} */

    function onSaveClick() {
        const p1 = password.password;
        const p2 = password.password2;

        const okPassword = (!p1 && !p2) || ((p1 || p2) && p1 === p2);
        if (!okPassword) {
            return;
        }

        const { firstname, lastname, email } = accountData;

        const tasks = [];
        tasks.push(API.AccountSet({ firstname, lastname, email }));
        p1 && p2 && tasks.push(API.AccountSetPassword({ password: p1 }));

        setWait(true);
        return Promise.all(tasks)
            .then(() => API.AccountCheck())
            .then(account => {
                if (!account?.ok) {
                    dispatch(setAccount(null));
                    history.push(Links.Login());
                    return;
                }

                dispatch(setAccount(account));
            })
            .finally(() => setWait(false));
    }

    function onDeleteClick() {
        setConfirmDelete({ show: true });
    }

    function onDeleteCancel() {
        setConfirmDelete({ show: false });
    }

    function onDeleteConfirm() {
        setWait(true);
        API.AccountDel()
            .then(res => {
                if (res?.errors) {
                    console.warn(res?.errors);
                    return;
                }

                if (!res?.ok) {
                    console.warn(`Unknown error while project deleting.`);
                    return;
                }
            })
            .then(() => setConfirmDelete({ ...ConfirmDelete, show: false }))
            .then(() => API.AccountCheck())
            .then(account => {
                // No problem (/account/check)
                if (!account?.ok) {
                    // console.warn("OK");
                    dispatch(setAccount(null));
                    history.push(Links.Login());
                    return;
                }

                console.warn("BAD");
                window.location.reload();
            })
            .finally(() => setWait(false));
    }

    function errorByField(fieldname) {
        return errors[fieldname] ? t(errors[fieldname]) : null;
    }

    const hasErrors = Object.values(errors).filter(v => v).length > 0;
    const hasNewPassword = !!(password.password || password.password2);
    const allowSave =
        Object.keys(accountData).length > 0 &&
        (JSON.stringify(accountData) !== prevAccountData ||
            (hasNewPassword && password.password === password.password2)) &&
        !hasErrors;

    return (
        <>
            <div className="panel-account">
                <h2 className="panel-heading-text">{t("EditAccount")}</h2>

                <div className="panel-account-fields">
                    <div>
                        <InputSelect
                            fullWidth
                            onSelect={handleLanguageChange}
                            options={Object.entries(locales).map(([value, text]) => ({ value, text }))}
                            selected={selectedLang}
                        />

                        <p className="field-message color-error">{errorByField("firstname") || <>&nbsp;</>}</p>
                    </div>

                    <div>
                        <InputBase
                            name="firstname"
                            onChange={onChange}
                            value={accountData.firstname || ""}
                            placeholder={t("Firstname")}
                        />

                        <p className="field-message color-error">{errorByField("firstname") || <>&nbsp;</>}</p>
                    </div>

                    <div>
                        <InputBase
                            name="lastname"
                            onChange={onChange}
                            value={accountData.lastname || ""}
                            placeholder={t("Lastname")}
                        />

                        <p className="field-message color-error">{errorByField("lastname") || <>&nbsp;</>}</p>
                    </div>

                    <div>
                        <InputBase
                            name="email"
                            onChange={onChange}
                            value={accountData.email || ""}
                            placeholder={t("Email")}
                        />

                        <p className="field-message color-error">{errorByField("email") || <>&nbsp;</>}</p>
                    </div>

                    <div>
                        <InputBase
                            type="password"
                            name="password"
                            onChange={onChange}
                            value={password.password || ""}
                            placeholder={t("Password")}
                        />

                        <p className="field-message color-error">{errorByField("password") || <>&nbsp;</>}</p>
                    </div>

                    <div>
                        <InputBase
                            type="password"
                            name="password2"
                            onChange={onChange}
                            value={password.password2 || ""}
                            placeholder={t("PasswordConfirm")}
                        />

                        <p className="field-message color-error">{errorByField("password2") || <>&nbsp;</>}</p>
                    </div>
                </div>

                <div className="panel-account-actions">
                    {/* <ButtonBase solid onClick={() => onCancelClick()} style={{ width: "180px" }}>
                        {t("Cancel")}
                    </ButtonBase> */}

                    <ButtonBase invert onClick={() => onSaveClick()} style={{ width: "164px" }} disabled={!allowSave}>
                        {t("Save")}
                    </ButtonBase>
                </div>

                <div className="panel-account-delete">
                    <ButtonBase link onClick={() => onDeleteClick()}>
                        {t("DeleteAccount")}
                    </ButtonBase>
                </div>
            </div>

            <Confirm
                show={ConfirmDelete?.show}
                header={t("DeletingTheAccount")}
                text={t("DeletingTheAccountMessage")}
                actions={[
                    { onClick: () => onDeleteCancel(), text: t("Cancel") },
                    {
                        onClick: () => onDeleteConfirm(),
                        text: t("Confirm"),
                        props: { invert: true },
                    },
                ]}
            />

            <Wait show={wait} />
        </>
    );
}
